import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { AiOutlineSend } from "react-icons/ai";


const AskAgent = () => {
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const chatEndRef = useRef(null);
  const chatBoxRef = useRef(null);
  const [hasStartedChat, setHasStartedChat] = useState(false); // 👈 Track if chat has started
  const [linkTopic, setLinkTopic] = useState('none');
  // Derived booleans used in rendering; keeps HMR safe if stale references linger.
  const showOmLink = linkTopic === 'about';
  const showYdelserLink = linkTopic === 'ydelser';
  const showCasesLink = linkTopic === 'kundecases';

  // Lightweight formatter for **bold** markers.
  const formatContent = (text) => {
    if (!text) return '';
    return text.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
  };

  const scrollToBottom = () => {
    if (chatBoxRef.current) {
      chatBoxRef.current.scrollTop = chatBoxRef.current.scrollHeight;
    } else {
      chatEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!input.trim()) return;

    setHasStartedChat(true); // 👈 Reveal the chat box after first submission

    const userMessage = { role: 'user', content: input };
    setMessages((prev) => [...prev, userMessage]);
    setInput('');
    setLoading(true);

    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API_URL || ''}/api/ask`,
        { question: input },
        { withCredentials: true }
      );

      const assistantMessage = {
        role: 'assistant',
        content: res.data.answer,
      };
      if (res.data.topic) {
        setLinkTopic(res.data.topic);
      } else {
        setLinkTopic('none');
      }
      setMessages((prev) => [...prev, assistantMessage]);
    } catch (err) {
      console.error(err);
      const errorMsg = {
        role: 'assistant',
        content: '❌ Error: Could not get a response. Please try again.',
      };
      setMessages((prev) => [...prev, errorMsg]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      width: '75%',
      margin: '2rem auto',
      padding: '1rem',
      borderRadius: '20px',
      background: 'linear-gradient(to bottom right, rgba(255, 255, 255, 0.94), rgba(251, 216, 216, 0.92))',
      boxShadow: '0 8px 20px rgba(192, 54, 38, 0.12)'
    }}>
      <style>{`
        .aice-chat-scroll {
          scrollbar-width: none;
          scrollbar-color: transparent transparent;
        }
        .aice-chat-scroll::-webkit-scrollbar {
          width: 0;
          height: 0;
        }
        .aice-chat-scroll::-webkit-scrollbar-thumb,
        .aice-chat-scroll::-webkit-scrollbar-track,
        .aice-chat-scroll::-webkit-scrollbar-button {
          display: none;
        }
      `}</style>

      {/* Show chat box only after the first message */}
      {hasStartedChat && (
        <div
            ref={chatBoxRef}
            className="aice-chat-scroll"
            style={{
              height: '300px',
              overflowY: 'auto',
              backgroundColor: 'transparent',
              padding: '1rem',
              borderRadius: '12px',
              marginBottom: '1rem',
              backdropFilter: 'blur(2px)'
            }}
          >
          {messages.map((msg, index) => (
            <div key={index} style={{
              display: 'flex',
              justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
              marginBottom: '0.75rem'
            }}>
              <div style={{
                maxWidth: '70%',
                backgroundColor: msg.role === 'user' ? '#FFE1D8' : '#FFF4F1',
                border: msg.role === 'user' ? '1px solid rgba(192, 54, 38, 0.18)' : '1px solid rgba(192, 54, 38, 0.12)',
                padding: '0.85rem 1rem',
                borderRadius: '14px',
                whiteSpace: 'pre-wrap',
                fontSize: '0.95rem',
                color: '#2A0D05',
                boxShadow: '0 6px 14px rgba(192, 54, 38, 0.06)',
              }}
              dangerouslySetInnerHTML={{ __html: formatContent(msg.content) }}
              />
            </div>
          ))}
          {(linkTopic === 'about' || linkTopic === 'ydelser' || linkTopic === 'kundecases') && (
            <div
              style={{
                display: 'flex',
                gap: '0.5rem',
                flexWrap: 'wrap',
                marginTop: '0.5rem',
                justifyContent: 'flex-start',
              }}
            >
              {linkTopic === 'about' && (
                <a
                  href="/about"
                  style={{
                    padding: '0.55rem 0.9rem',
                    background: 'linear-gradient(135deg, #C03626, #FF6F61)',
                    color: '#fff',
                    borderRadius: '10px',
                    textDecoration: 'none',
                    fontWeight: 600,
                    boxShadow: '0 6px 16px rgba(192, 54, 38, 0.3)',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.4rem',
                    whiteSpace: 'nowrap',
                  }}
                >
                  Se mere på Om-siden
                </a>
              )}
              {linkTopic === 'ydelser' && (
                <a
                  href="/ydelser"
                  style={{
                    padding: '0.55rem 0.9rem',
                    background: 'linear-gradient(135deg, #C03626, #FF6F61)',
                    color: '#fff',
                    borderRadius: '10px',
                    textDecoration: 'none',
                    fontWeight: 600,
                    boxShadow: '0 6px 16px rgba(192, 54, 38, 0.3)',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.4rem',
                    whiteSpace: 'nowrap',
                  }}
                >
                  Se ydelser
                </a>
              )}
              {linkTopic === 'kundecases' && (
                <a
                  href="/kundecases"
                  style={{
                    padding: '0.55rem 0.9rem',
                    background: 'linear-gradient(135deg, #C03626, #FF6F61)',
                    color: '#fff',
                    borderRadius: '10px',
                    textDecoration: 'none',
                    fontWeight: 600,
                    boxShadow: '0 6px 16px rgba(192, 54, 38, 0.3)',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.4rem',
                    whiteSpace: 'nowrap',
                  }}
                >
                  Se kundecases
                </a>
              )}
            </div>
          )}
          {loading && (
            <div style={{ fontStyle: 'italic', color: '#999' }}>🤖 AICE is thinking...</div>
          )}
          <div ref={chatEndRef} />
        </div>
      )}

      <form onSubmit={handleSubmit} style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Har du et spørgsmål? Vores AI-kollega står klar til at hjælpe"
          style={{
            flex: 1,
            padding: '0.5rem',
            fontSize: '1rem',
            borderRadius: '8px',
            border: '0px solid rgba(192, 54, 38, 0.25)',
            backgroundColor: 'transparent',
            outline: 'none',          // ✅ removes focus ring
            boxShadow: 'none',        // ✅ prevents browser glow
            color: 'black',           // optional: adjust text color for visibility
          }}
        />

        <button
          type="submit"
          disabled={loading}
          style={{
            background: 'linear-gradient(135deg, #C03626, #FF6F61)',
            color: '#fff',
            padding: '0.75rem 1.25rem',
            borderRadius: '6px',
            border: 'none',
            cursor: loading ? 'not-allowed' : 'pointer'
          }}
        >
          <AiOutlineSend
            size={20}
          />
        </button>
      </form>

      {(linkTopic === 'about' || linkTopic === 'ydelser' || linkTopic === 'kundecases') && (
        <div style={{ height: '3rem' }} aria-hidden="true" />
      )}
    </div>
  );
};

export default AskAgent;
